import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { logout } from '../../store/slices/authSlice';
import { updateUserName } from '../../store/modules/userData';

// Keys to clear on logout — centralized for easy maintenance
const PERSISTED_STORAGE_KEYS = [
  'token',
  'refreshToken',
  'persist:user',
  'persist:imgurl',
  'persist:itemdata',
  'persist:auth',
  'MeAsmarketerId',
  'MeAsmarketerUserId',
  'MarketerFor'
];

const getCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const getApiBaseUrl = () => {
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    const envUrl = process.env.REACT_APP_API_URL;
    if (
      !envUrl ||
      envUrl.includes('localhost') ||
      envUrl.includes('127.0.0.1')
    ) {
      return 'http://localhost:8080/api';
    }
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
};

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    // Prefer Redux persisted token, fall back to localStorage
    const token = getState()?.auth?.token || localStorage.getItem('token');

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Attach CSRF Token if present in cookies
    const csrfToken = getCookie('csrfToken');
    if (csrfToken) {
      headers.set('x-csrf-token', csrfToken);
    }

    return headers;
  },
});

/**
 * Track whether a logout was already dispatched this session to avoid
 * cascading logouts from multiple simultaneous 401 responses.
 */
let isLoggingOut = false;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result?.error && result.error.status === 401) {
    // Guard: only trigger logout once per session to avoid multiple
    // simultaneous 401 responses (e.g. from refetchOnFocus) all firing logout.
    if (!isLoggingOut) {
      isLoggingOut = true;

      // Check if we actually have a token — if we never had one, don't bother
      // 1. Dispatch logout actions to clear Redux state
      api.dispatch(logout());
      api.dispatch(updateUserName(''));
      api.dispatch(apiSlice.util.resetApiState());

      // 2. Clear persisted data from localStorage
      PERSISTED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));

      // Reset the guard after a short delay so future intentional
      // login attempts can trigger a new session check if needed.
      setTimeout(() => {
        isLoggingOut = false;
      }, 3000);
    }
  }

  return result;
};

export const TAG_TYPES = {
  USER: 'User',
  USER_INFO: 'UserInfo',
  USER_CONNECTIONS: 'UserConnections',
  CONNECTIONS: 'Connections',
  TIMELINE: 'Timeline',
  SHOP: 'Shop',
  VISIT_PROFILE: 'VisitProfile',
  SHOP_ITEMS: 'ShopItems',
  SUBSCRIPTION_MARKETING: 'SubscriptionMarketing',
  MARKETERS: 'Marketers',
  MARKETER: 'Marketer',
  MARKETER_AVAILABILITY: 'MarketerAvailability',
  HIRE_REQUEST: 'HireRequest',
  ALLOWED_ZONES: 'AllowedZones',
  SHOP_RATING: 'ShopRating',
  CV_FORM: 'CvForm',
  EMPLOYEE: 'Employee',
  TRANSPORT_VEHICLE: 'TransportVehicle',
  USER_PROPERTIES: 'UserProperties',
  USER_COMPANIES: 'UserCompanies',
  PROPERTY: 'Property',
  NOTIFICATIONS: 'Notifications',
  CONSULTANCY: 'Consultancy',
  CHAT_BLOCK: 'ChatBlock',
  LEVEL_RANKING: 'LevelRanking',
  SHOP_VERIFICATION: 'ShopVerification',
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: Object.values(TAG_TYPES),
  // refetchOnFocus can cause a flood of requests right after login.
  // Keep it disabled to avoid triggering 401 cascades on mount.
  refetchOnFocus: false,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});
